import { observable, computed, action, reaction } from 'mobx';
import { User, Lead } from '.';
import { app } from './../App';
import {
  IProviderDetails,
  ILeadGroupInfo,
  ILeadGroup,
  ILeadGroupDropdown,
  LeadGroupModalStatus,
} from 'common/interfaces/provider';

export class LeadGroup {
  constructor(private user: User, private lead: Lead) {}

  private reactToLeadChange = reaction(
    () => this.lead.openLeadId,
    (leadId: number) => {
      if (leadId !== null) {
        this.fetchLeadGroups();
      }
    }
  );

  private reactToLeadDetailsChange = reaction(
    () => this.lead.openLeadDetails,
    (details: IProviderDetails) => {
      if (details !== null) {
        if (
          details.providerGroup !== null &&
          typeof details.providerGroup.id !== 'undefined' &&
          details.providerGroup.id
        ) {
          this.fetchLeadGroupDetails(details.providerGroup.id);
        }

        if (this.groupDropownForLeadOfId !== details.databaseId) {
          this.changeBusinessGroupDropdown(
            details.databaseId,
            typeof details.providerGroup !== 'undefined' &&
              details.providerGroup !== null &&
              typeof details.providerGroup.name !== 'undefined'
              ? details.providerGroup.name
              : null
          );
        }
      }
    }
  );

  @observable public openedGroup: ILeadGroup = null;
  @observable public modalStatus: LeadGroupModalStatus =
    LeadGroupModalStatus.Closed;
  @observable public leadGroups: ILeadGroup[] = [];
  @observable public loading: boolean = false;
  @observable public showList: boolean = false;
  @observable public createNewGroup: string = '';
  @observable public groupListSearch: string = '';

  @observable public groupDropdown: ILeadGroupDropdown[] = [];
  @observable private groupDropownForLeadOfId: number = null;

  @action public changeBusinessGroupDropdown = (
    leadId: number,
    groupName: string | null = null
  ) => {
    this.groupDropownForLeadOfId = leadId;
    if (groupName) {
      this.groupDropdown = [
        { value: 'remove', label: 'Remove from group "' + groupName + '"' },
        { value: 'name', label: 'Keep in group "' + groupName + '"' },
        { value: 'assign', label: 'Assign to different group' },
      ];
    } else {
      this.groupDropdown = [
        { value: 'name', label: 'No group' },
        { value: 'assign', label: 'Assign to a group' },
      ];
    }
  };

  // States search results
  @computed public get groupSearchedResults(): ILeadGroup[] {
    const searchQuery: string = this.groupListSearch.toLowerCase().trim();

    if (searchQuery.length > 0) {
      return this.leadGroups.filter(({ name }) => {
        return name.toLowerCase().indexOf(searchQuery) !== -1;
      });
    } else {
      return this.leadGroups;
    }
  }

  @action public patchLeadGroup = async (): Promise<any> => {
    try {
      this.loading = true;
      const patchGroup: any = app.service('provider-group');
      const response = await patchGroup.patch(1, {
        name: 'Name #1',
      });
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      this.loading = false;
    }
  };

  @action public fetchLeadGroupDetails = async (
    id: number
  ): Promise<ILeadGroup> => {
    try {
      this.loading = true;

      const leadGroupDetails: any = app.service('provider-group');
      const response: ILeadGroup = await leadGroupDetails.get(id);

      this.openedGroup = response;

      return response;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      this.loading = false;
    }
  };

  @action public fetchLeadGroups = async (): Promise<ILeadGroup[]> => {
    try {
      this.loading = true;

      const leadGroups = app.service('provider-group');
      const response = (await leadGroups.find()) as ILeadGroupInfo;

      this.leadGroups = response.data;

      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      this.loading = false;
    }
  };

  @action public createLeadGroup = async (): Promise<ILeadGroup> => {
    try {
      this.loading = true;

      const leadGroups = app.service('provider-group');
      const response = (await leadGroups.create({
        name: this.createNewGroup,
      })) as ILeadGroup;

      this.createNewGroup = '';
      this.modalStatus = LeadGroupModalStatus.OpenedOnGroupList;

      await this.addLeadToGroup(response.id);

      this.lead.openLeadDetails.providerGroup = {
        id: response.id,
        name: response.name,
      };

      return response;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      this.loading = false;
    }
  };

  @action public addLeadToGroup = async (
    groupId: number | null
  ): Promise<IProviderDetails> => {
    try {
      this.loading = true;

      const leadGroups = app.service('provider');
      const response = (await leadGroups.patch(this.lead.openLeadId, {
        groupId,
      })) as IProviderDetails;

      this.createNewGroup = '';
      this.modalStatus = LeadGroupModalStatus.Closed;

      setTimeout(() => {
        this.groupListSearch = '';
      }, 256);

      this.lead.openLeadDetails.providerGroup = response.providerGroup
        ? {
            id: response.providerGroup.id,
            name: response.providerGroup.name,
          }
        : null;

      this.changeBusinessGroupDropdown(
        response.databaseId,
        response.providerGroup ? response.providerGroup.name : null
      );

      return response;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      this.loading = false;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/LeadGroup.ts